import {
  BufferGeometry,
  Float32BufferAttribute,
  Points,
  PointsMaterial,
  Uint8BufferAttribute,
  Vector3,
} from 'three';

import { WebApi } from '../../../../../services/web-api';

type LasHeader = {
  pointDataOffset: number;
  pointFormat: number;
  pointRecordLength: number;
  numPoints: number;

  xScale: number;
  yScale: number;
  zScale: number;
  xOffset: number;
  yOffset: number;
  zOffset: number;
};

function readString(dv: DataView, o: number, len: number) {
  let s = '';
  for (let i = 0; i < len; i++) {
    const c = dv.getUint8(o + i);
    if (c === 0) {
      break;
    }
    s += String.fromCharCode(c);
  }
  return s;
}

function parseLasHeader(dv: DataView): LasHeader {
  // LAS signature "LASF" at 0
  const sig = readString(dv, 0, 4);
  if (sig !== 'LASF') {
    throw new Error('Not a LAS file (missing LASF header)');
  }

  // These offsets are standard for LAS 1.0–1.3. (LAS 1.4 adds more, but these are still present.)
  const headerSize = dv.getUint16(94, true);
  const pointDataOffset = dv.getUint32(96, true);
  const pointFormat = dv.getUint8(104);
  const pointRecordLength = dv.getUint16(105, true);

  const legacyNumPoints = dv.getUint32(107, true);

  // scale/offset are float64 at 131.. (standard)
  const xScale = dv.getFloat64(131, true);
  const yScale = dv.getFloat64(139, true);
  const zScale = dv.getFloat64(147, true);

  const xOffset = dv.getFloat64(155, true);
  const yOffset = dv.getFloat64(163, true);
  const zOffset = dv.getFloat64(171, true);

  // For LAS 1.4, legacyNumPoints can be 0 and the "extended number of points" is later.
  // We'll fall back to legacy unless you add LAS 1.4 parsing.
  const numPoints = legacyNumPoints;

  if (headerSize <= 0 || pointDataOffset <= 0 || pointRecordLength <= 0) {
    throw new Error('LAS header looks invalid');
  }
  if (!numPoints) {
    throw new Error('LAS has 0 points (or LAS 1.4 extended counts not handled)');
  }

  return {
    pointDataOffset,
    pointFormat,
    pointRecordLength,
    numPoints,
    xScale,
    yScale,
    zScale,
    xOffset,
    yOffset,
    zOffset,
  };
}

function getRgbOffset(pointFormat: number): number | null {
  // Common LAS formats with RGB:
  // fmt2: RGB at byte 20
  // fmt3: GPS time (8 bytes) before RGB => RGB at byte 28
  if (pointFormat === 2) {
    return 20;
  }
  if (pointFormat === 3) {
    return 28;
  }
  return null;
}

export async function loadLasPoints(url: string, origo: Vector3) {
  const buffer = await WebApi.fetchS3DataAsArrayBuffer(url);
  const dv = new DataView(buffer);
  const h = parseLasHeader(dv);

  const rgbOffset = getRgbOffset(h.pointFormat);
  const hasRgb = rgbOffset != null;

  const positions = new Float32Array(h.numPoints * 3);
  // Raw 16-bit RGB read straight from the file. We keep the raw values until
  // we've scanned them all, because we can't know the effective bit depth of a
  // channel until we've seen its maximum (see below).
  const rgb16 = hasRgb ? new Uint16Array(h.numPoints * 3) : null;
  let maxChannel = 0;

  let base = h.pointDataOffset;

  for (let i = 0; i < h.numPoints; i++) {
    const X = dv.getInt32(base + 0, true);
    const Y = dv.getInt32(base + 4, true);
    const Z = dv.getInt32(base + 8, true);

    const x = X * h.xScale + h.xOffset - origo.x;
    const y = Y * h.yScale + h.yOffset - origo.y;
    const z = Z * h.zScale + h.zOffset - origo.z;

    const p = i * 3;
    positions[p + 0] = x;
    positions[p + 1] = y;
    positions[p + 2] = z;

    if (hasRgb && rgb16 && rgbOffset != null) {
      const r16 = dv.getUint16(base + rgbOffset + 0, true);
      const g16 = dv.getUint16(base + rgbOffset + 2, true);
      const b16 = dv.getUint16(base + rgbOffset + 4, true);

      rgb16[p + 0] = r16;
      rgb16[p + 1] = g16;
      rgb16[p + 2] = b16;

      if (r16 > maxChannel) {
        maxChannel = r16;
      }
      if (g16 > maxChannel) {
        maxChannel = g16;
      }
      if (b16 > maxChannel) {
        maxChannel = b16;
      }
    }

    base += h.pointRecordLength;
  }

  let colorsU8: Uint8Array | null = null;

  if (rgb16) {
    colorsU8 = new Uint8Array(h.numPoints * 3);

    // The LAS spec stores RGB in 16-bit fields, but many photogrammetry
    // exporters write plain 8-bit values (0–255) into them without scaling up.
    // Assuming full 16-bit and dividing by 65535 collapses those to near-black.
    // Detect the real range: if nothing exceeds 255 the data is 8-bit, so scale
    // by 255; otherwise it's true 16-bit and we scale by 65535.
    const isEightBit = maxChannel <= 255;
    const scale = isEightBit ? 255 : 65535;
    const to8 = (c: number) => ((c * 255 + scale / 2) / scale) | 0;

    // The stored colours are display-referred sRGB. three.js does not colour-
    // manage vertex-colour attributes (unlike textures, which we tag
    // SRGBColorSpace), so the shader consumes them directly in the linear
    // working space. Decode sRGB → linear here so points match the sRGB
    // orthophoto rendered alongside them.
    const sRGB8ToLinear8 = (u8: number) => {
      const cs = u8 / 255;
      const cl = cs <= 0.04045 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4);
      return Math.min(255, Math.max(0, Math.round(cl * 255)));
    };

    for (let p = 0; p < colorsU8.length; p++) {
      colorsU8[p] = sRGB8ToLinear8(to8(rgb16[p]));
    }
  }

  const geom = new BufferGeometry();
  geom.setAttribute('position', new Float32BufferAttribute(positions, 3));

  if (colorsU8) {
    const attr = new Uint8BufferAttribute(colorsU8, 3);
    attr.normalized = true;
    geom.setAttribute('color', attr);
  }

  geom.computeBoundingSphere();

  return new Points(
    geom,
    new PointsMaterial({
      vertexColors: !!colorsU8,
      sizeAttenuation: false,
      size: 4.0,
    }),
  );
}
